import { ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../../ui/button';
import { Input } from '../../../../ui/input';
import type { BaseFieldProps, ListFieldSchema } from '../../../types';

export function ListFieldInput({
  field,
  value = [],
  onChange,
  error,
}: BaseFieldProps<ListFieldSchema, string[] | null | undefined>) {
  const items = Array.isArray(value) ? value : [];

  const handleAddItem = () => {
    onChange?.([...items, '']);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange?.(newItems);
  };

  const handleItemChange = (index: number, val: string) => {
    const newItems = [...items];
    newItems[index] = val;
    onChange?.(newItems);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    const temp = newItems[index];
    newItems[index] = newItems[targetIndex];
    newItems[targetIndex] = temp;
    onChange?.(newItems);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-2 group animate-in fade-in slide-in-from-top-1 duration-200"
          >
            <div className="text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors">
              <GripVertical className="w-4 h-4" />
            </div>
            <Input
              value={item}
              onChange={(e) => handleItemChange(index, e.target.value)}
              placeholder={field.itemPlaceholder || 'Option...'}
              className="flex-1"
            />
            <div className="flex items-center border rounded-md overflow-hidden bg-background">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-none border-r"
                disabled={index === 0}
                onClick={() => moveItem(index, 'up')}
              >
                <ArrowUp className="w-3 h-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-none border-r"
                disabled={index === items.length - 1}
                onClick={() => moveItem(index, 'down')}
              >
                <ArrowDown className="w-3 h-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-none text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => handleRemoveItem(index)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center p-6 border-2 border-dashed rounded-lg bg-muted/30">
          <p className="text-sm text-muted-foreground mb-3">No items added yet</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Add First Item
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddItem}
          className="w-full border-dashed gap-2 hover:bg-muted/50"
        >
          <Plus className="w-4 h-4" />
          Add Option
        </Button>
      )}

      {error && <p className="text-xs font-medium text-destructive mt-1">{error}</p>}
    </div>
  );
}

export function ListFieldView({
  value,
}: BaseFieldProps<ListFieldSchema, string[] | null | undefined>) {
  const items = Array.isArray(value) ? value : [];

  if (items.length === 0) {
    return <span className="text-muted-foreground text-sm">Empty list</span>;
  }

  return (
    <ul className="list-disc list-inside space-y-1">
      {items.map((item, index) => (
        <li key={index} className="text-sm font-medium">
          {item || <span className="italic text-muted-foreground opacity-50">(Empty)</span>}
        </li>
      ))}
    </ul>
  );
}
